import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Crown, Tv, Gamepad2, Baby, ShieldCheck, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { HeroBanner } from "@/components/HeroBanner";
import { ContentRow } from "@/components/ContentRow";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const FEATURES = [
  { icon: Tv, title: "5,000+ Live Channels", text: "News, Sports, Shows, Drama & more from the USA, UK, Africa & Australia." },
  { icon: Gamepad2, title: "Classic Games", text: "Play classic arcade & retro games right in your browser — no downloads." },
  { icon: Baby, title: "3 Profiles & Kids Mode", text: "Up to 3 profiles per account, watch on 3 screens, with a safe Kids space." },
  { icon: ShieldCheck, title: "100% Free", text: "Everything unlocked for registered members. No plans, no card, no catch." },
];

function PremiumBand() {
  const { display, converted } = useCurrency();
  return (
    <div data-testid="premium-band" className="mx-4 sm:mx-8 lg:mx-12 rounded-2xl overflow-hidden relative bg-gradient-to-r from-brand/30 via-[#1a1016] to-black border border-gold/20 p-6 sm:p-8">
      <div className="absolute -right-20 -top-20 w-72 h-72 bg-gold/20 rounded-full blur-[100px]" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
        <div>
          <div className="inline-flex items-center gap-2 text-gold text-xs font-bold uppercase tracking-widest mb-2"><Crown size={14} /> NeTimeTV Subscription</div>
          <h3 className="font-head text-2xl sm:text-3xl font-extrabold">Unlimited HD streaming — {display}/mo</h3>
          <p className="text-zinc-400 text-sm mt-1">All live TV, games, Kids Mode & 4K · 3 screens · cancel anytime.{converted && " Billed as R24.99 ZAR."}</p>
        </div>
        <Link to="/subscribe" data-testid="band-subscribe-btn"
          className="shrink-0 inline-flex items-center gap-2 bg-gradient-to-r from-gold to-amber-500 text-black font-bold px-7 py-3 rounded-xl hover:brightness-110 transition self-start">
          <Crown size={18} /> Subscribe
        </Link>
      </div>
    </div>
  );
}

function FreeBand() {
  const { t } = useTranslation();
  return (
    <div data-testid="free-band" className="mx-4 sm:mx-8 lg:mx-12 rounded-2xl overflow-hidden relative bg-gradient-to-r from-emerald-600/25 via-[#10161a] to-black border border-emerald-500/25 p-6 sm:p-8">
      <div className="absolute -right-20 -top-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-[100px]" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
        <div>
          <div className="inline-flex items-center gap-2 text-emerald-300 text-xs font-bold uppercase tracking-widest mb-2"><Sparkles size={14} /> {t("free.badge")}</div>
          <h3 className="font-head text-2xl sm:text-3xl font-extrabold">{t("free.subTitle")}</h3>
          <p className="text-zinc-400 text-sm mt-1">{t("free.subBody")}</p>
        </div>
        <Link to="/register" data-testid="band-free-signup-btn"
          className="shrink-0 inline-flex items-center gap-2 bg-emerald-500 text-black font-bold px-7 py-3 rounded-xl hover:bg-emerald-400 transition-colors self-start">
          {t("free.cta")}
        </Link>
      </div>
    </div>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  const [continueItems, setContinueItems] = useState([]);
  const { user, subscribed, kids } = useAuth();
  const { freeAccess } = useCurrency();

  useEffect(() => {
    setData(null);
    api.get("/content/home", { params: { kids } }).then(({ data }) => setData(data)).catch(() => setData({ hero: null, rows: [] }));
  }, [kids]);

  useEffect(() => {
    if (user) api.get("/me/history").then(({ data }) => setContinueItems(data.items)).catch(() => {});
  }, [user]);

  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-brand" size={40} /></div>;

  return (
    <div className="pb-20">
      <div className="relative">
        <HeroBanner item={data.hero} />
      </div>
      <div className="relative z-20 mt-6 sm:mt-10 space-y-10 sm:space-y-14">
        {continueItems.length > 0 && <ContentRow title="Continue Watching" items={continueItems} wide />}
        {data.rows.slice(0, 2).map((row, i) => <ContentRow key={i} title={row.title} items={row.items} seeAll={row.see_all} />)}
        {freeAccess ? (user === false && <FreeBand />) : (!subscribed && <PremiumBand />)}
        {data.rows.slice(2).map((row, i) => <ContentRow key={i + 2} title={row.title} items={row.items} seeAll={row.see_all} />)}

        <section className="px-4 sm:px-8 lg:px-12 pt-6">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="text-gold" size={22} />
            <h2 className="font-head text-2xl sm:text-3xl font-bold">Why NeTimeTV</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-card border border-white/10 rounded-2xl p-6 hover:border-brand/40 transition-colors">
                <span className="w-11 h-11 grid place-items-center rounded-xl bg-brand/20 text-brand mb-4"><f.icon size={20} /></span>
                <h3 className="font-head font-bold text-lg mb-1">{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
